import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Ruler, Users, Bed } from "@phosphor-icons/react";
import Reveal from "./Reveal";
import { useLang } from "../context/LanguageContext";

export default function RoomsShowcase({ rooms = [] }) {
  const { t, r, lang } = useLang();

  return (
    <section className="relative py-24 lg:py-40 bg-[#09090b]" data-testid="rooms-showcase">
      <div className="container-x">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16">
          <Reveal>
            <div className="overline mb-4">{t.rooms.overline}</div>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1] max-w-xl">
              {t.rooms.title.split("\n").map((l, i) => (
                <span key={l || `rooms-line-${i}`} className="block">{l}</span>
              ))}
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="max-w-md text-white/70 text-base leading-relaxed">{t.rooms.subtitle}</p>
          </Reveal>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {rooms.map((room, i) => {
            const name = lang === "it" ? room.name_it : room.name_en;
            const short = lang === "it" ? room.short_it : room.short_en;
            const slug = lang === "it" ? room.slug_it : room.slug_en;
            const badge = lang === "it" ? room.badge_it : room.badge_en;
            return (
              <Reveal key={room.id} delay={i * 0.1}>
                <Link
                  to={r.room(slug)}
                  data-testid={`room-card-${slug}`}
                  className="card-hover group block relative overflow-hidden border border-white/10 hover:border-[#C5A059]/50 transition-colors"
                >
                  <div className="relative aspect-[4/5] overflow-hidden bg-[#18181b] on-image">
                    <img src={room.cover} alt={name} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                    {badge && (
                      <div className="absolute top-5 left-5 text-[10px] uppercase tracking-[0.25em] font-medium bg-[#C5A059] text-black px-3 py-1.5">
                        {badge}
                      </div>
                    )}
                    <div className="absolute top-5 right-5 w-10 h-10 rounded-full border border-white/30 flex items-center justify-center backdrop-blur-sm bg-black/20 group-hover:bg-[#C5A059] group-hover:border-[#C5A059] transition-all">
                      <ArrowUpRight size={16} className="text-white group-hover:text-black" />
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 p-6 lg:p-7">
                      <div className="overline mb-2 text-white/70">
                        {t.rooms.from} €{room.price_from} · {t.rooms.perNight}
                      </div>
                      <h3 className="font-serif text-3xl lg:text-4xl leading-tight">{name}</h3>
                      <p className="mt-3 text-sm text-white/75 leading-relaxed max-w-sm">{short}</p>
                      <div className="mt-5 flex items-center gap-5 text-xs text-white/60">
                        <span className="inline-flex items-center gap-1.5"><Ruler size={14} /> {room.size_sqm}m²</span>
                        <span className="inline-flex items-center gap-1.5"><Users size={14} /> {room.occupancy}</span>
                        <span className="inline-flex items-center gap-1.5"><Bed size={14} /> {lang === "it" ? room.bed_type_it : room.bed_type_en}</span>
                      </div>
                    </div>
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
